// TanStack Query hooks for listing, metadata, folder creation, and
// download-URL. Runtime calls go through the openapi-fetch typed
// client (which attaches Bearer auth + refresh-on-401); response
// shapes are the generated OpenAPI components re-exported from
// ``types.ts`` for component-level convenience.
//
// Cache keys all live under ``["files", ...]`` so a single
// ``invalidateQueries({ queryKey: ["files"] })`` after any mutation
// (rename / move / delete / restore / upload / new folder) refreshes
// every listing, the trash, the move-dialog tree and breadcrumbs.
//
// Listings are paginated server-side (ADR 0018: default page 100,
// opaque ``next_cursor``). Folder and trash listings use infinite
// queries with an explicit "Load more"; the move dialog needs every
// sub-folder up front and walks all pages via ``fetchAllPages``.

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";

import { useAuth } from "@/auth/stub";

import { api } from "./client";
import { storageKeys } from "./storage";
import { errorDetail } from "@/lib/apiError";

import type { DownloadResponse, FileEntry, FileListResponse } from "./types";

function detail(err: unknown): string {
  return errorDetail(err, "request failed");
}

export const fileKeys = {
  all: ["files"] as const,
  list: (parentId: string | null, userId: string | null) =>
    ["files", "list", { parentId, userId }] as const,
  entry: (fileId: string | null, userId: string | null) =>
    ["files", "entry", { fileId, userId }] as const,
  trash: (userId: string | null) => ["files", "trash", { userId }] as const,
  folders: (parentId: string | null, userId: string | null) =>
    ["files", "folders", { parentId, userId }] as const,
  sharedWithMe: (userId: string | null) =>
    ["files", "shared-with-me", { userId }] as const,
  sharedByMe: (userId: string | null) =>
    ["files", "shared-by-me", { userId }] as const,
};

/** One page of a cursor-paginated listing. */
export interface Page<T> {
  items: T[];
  next_cursor?: string | null;
}

/**
 * Flatten infinite-query pages into one list, dropping repeats.
 *
 * Cursors are offsets (ADR 0018), so an insert or delete between two
 * page fetches can shift a row onto the next page as well; keeping
 * the first occurrence avoids rendering it twice (duplicate React
 * keys, double-counted selection).
 */
export function mergePages<T extends { id: string }>(
  pages: readonly Page<T>[] | undefined,
): T[] {
  if (!pages) return [];
  const seen = new Set<string>();
  const out: T[] = [];
  for (const page of pages) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
  }
  return out;
}

/**
 * Follow ``next_cursor`` until the server says there is no more.
 *
 * ``maxPages`` is a runaway guard (a server bug that keeps returning
 * a cursor must not spin forever); at the default page size it still
 * covers 20k entries.
 */
export async function fetchAllPages<T>(
  fetchPage: (cursor: string | null) => Promise<Page<T>>,
  maxPages = 200,
): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < maxPages; i += 1) {
    const page: Page<T> = await fetchPage(cursor);
    out.push(...page.items);
    cursor = page.next_cursor ?? null;
    if (cursor === null) return out;
  }
  throw new Error("listing did not terminate (too many pages)");
}

/** A 404 from the API: the item is gone, or never was visible to us. */
export class NotFoundError extends Error {}

/** Retry transient failures, never a 404. */
function retryUnlessNotFound(failureCount: number, err: unknown): boolean {
  return !(err instanceof NotFoundError) && failureCount < 3;
}

async function fetchFilesPage(
  parentId: string | null,
  cursor: string | null,
): Promise<FileListResponse> {
  const query: { parent_id?: string; cursor?: string } = {};
  if (parentId !== null) query.parent_id = parentId;
  if (cursor !== null) query.cursor = cursor;
  const { data, error, response } = await api.GET("/api/v1/files", {
    params: { query },
  });
  if (error) {
    if (response.status === 404) throw new NotFoundError(detail(error));
    throw new Error(detail(error));
  }
  return data as FileListResponse;
}

async function fetchTrashPage(
  cursor: string | null,
): Promise<FileListResponse> {
  const { data, error } = await api.GET("/api/v1/trash", {
    params: { query: cursor !== null ? { cursor } : {} },
  });
  if (error) {
    throw new Error(detail(error));
  }
  return data as FileListResponse;
}

const nextCursor = (last: FileListResponse): string | null =>
  last.next_cursor ?? null;

/** Folder listing, one server page at a time (see ``mergePages``). */
export function useFileList(parentId: string | null) {
  const userId = useAuth((s) => s.user?.id ?? null);
  return useInfiniteQuery({
    queryKey: fileKeys.list(parentId, userId),
    enabled: userId !== null,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchFilesPage(parentId, pageParam),
    getNextPageParam: nextCursor,
    retry: retryUnlessNotFound,
  });
}

/** Trashed items, one server page at a time. */
export function useTrash() {
  const userId = useAuth((s) => s.user?.id ?? null);
  return useInfiniteQuery({
    queryKey: fileKeys.trash(userId),
    enabled: userId !== null,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchTrashPage(pageParam),
    getNextPageParam: nextCursor,
  });
}

/**
 * Every direct child of ``parentId`` (all pages). For the move-dialog
 * tree, which must offer every folder as a destination.
 */
export function useAllChildren(parentId: string | null, enabled = true) {
  const userId = useAuth((s) => s.user?.id ?? null);
  return useQuery({
    queryKey: fileKeys.folders(parentId, userId),
    enabled: enabled && userId !== null,
    // Always refetch when a picker node mounts (i.e. the dialog
    // opens): showing a folder that was meanwhile moved or trashed
    // would produce a confusing 404 on submit.
    staleTime: 0,
    queryFn: () =>
      fetchAllPages<FileEntry>((cursor) => fetchFilesPage(parentId, cursor)),
  });
}

export function useFile(fileId: string | null) {
  const userId = useAuth((s) => s.user?.id ?? null);
  return useQuery({
    queryKey: fileKeys.entry(fileId, userId),
    enabled: userId !== null && fileId !== null,
    queryFn: async (): Promise<FileEntry> => {
      if (fileId === null) {
        throw new Error("useFile called with null id (enabled guard bypassed)");
      }
      const { data, error, response } = await api.GET(
        "/api/v1/files/{file_id}",
        {
          params: { path: { file_id: fileId } },
        },
      );
      if (error) {
        if (response.status === 404) throw new NotFoundError(detail(error));
        throw new Error(detail(error));
      }
      return data as FileEntry;
    },
    retry: retryUnlessNotFound,
  });
}

/**
 * Callback that marks every file-shaped query stale and refetches the
 * active ones. Call after any successful mutation of the file tree.
 */
export function useInvalidateFiles(): () => Promise<void> {
  const qc = useQueryClient();
  return useCallback(async () => {
    // Usage moves with every file change (sidebar meter, Storage page).
    await Promise.all([
      qc.invalidateQueries({ queryKey: fileKeys.all }),
      qc.invalidateQueries({ queryKey: storageKeys.all }),
    ]);
  }, [qc]);
}

export function useCreateFolder(parentId: string | null) {
  const invalidateFiles = useInvalidateFiles();
  return useMutation({
    mutationFn: async (name: string): Promise<FileEntry> => {
      const { data, error } = await api.POST("/api/v1/folders", {
        body: { name, parent_id: parentId },
      });
      if (error) {
        throw new Error(detail(error));
      }
      return data as FileEntry;
    },
    onSuccess: () => invalidateFiles(),
  });
}

export async function requestDownloadUrl(
  fileId: string,
): Promise<DownloadResponse> {
  const { data, error } = await api.GET("/api/v1/files/{file_id}/download", {
    params: { path: { file_id: fileId } },
  });
  if (error) {
    throw new Error(detail(error));
  }
  return data as DownloadResponse;
}

/**
 * The bytes of a file's current version, for the built-in viewer.
 *
 * Fetched through the same scoped download URL a download uses (the
 * response is ``attachment`` + sandboxed, which ``fetch`` ignores);
 * the viewer decides how to show them. Keyed on ``updated_at`` so a
 * new version is fetched fresh, and kept out of the ``["files"]``
 * family so listing invalidations don't re-download it.
 */
export function useFileContent(file: FileEntry | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["file-content", file?.id ?? null, file?.updated_at ?? null],
    enabled: enabled && file !== undefined,
    staleTime: Infinity,
    gcTime: 60_000,
    queryFn: async (): Promise<Uint8Array<ArrayBuffer>> => {
      if (file === undefined) {
        throw new Error("useFileContent called without a file");
      }
      const { url } = await requestDownloadUrl(file.id);
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) {
        throw new Error(`download failed (${res.status})`);
      }
      return new Uint8Array(await res.arrayBuffer());
    },
  });
}
